import { useMemo, useRef, useState } from 'react';
import { UMIcon, UMInput, UMSegmentedControl } from '../../urbanmap/_components';
import { Chip, chipListStyle } from './Chip';
import { PC, UMR, caption, card } from './tokens';
import type { HeatNetwork, ProjectKind } from './types';

interface Props {
  kind: ProjectKind;
  onKindChange: (k: ProjectKind) => void;
  networks: HeatNetwork[];
  selected: string[];
  onSelectedChange: (ids: string[]) => void;
  /** Communes du périmètre : leurs réseaux sont proposés en premier. */
  communes: string[];
}

const KIND_LABEL: Record<ProjectKind, string> = {
  new: 'Nouveau réseau',
  extension: 'Réseau(x) existant(s)',
};

const MAX_SUGGESTIONS = 6;
const norm = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
const label = (n: HeatNetwork) => `${n.code} — ${n.name}`;

/** Type de projet et, pour un développement, recherche des réseaux repris (code + nom). */
export function NetworkPicker({ kind, onKindChange, networks, selected, onSelectedChange, communes }: Props) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const blurTimer = useRef<ReturnType<typeof setTimeout>>();

  const byId = useMemo(() => new Map(networks.map((n) => [n.id, n])), [networks]);
  /** Les réseaux du périmètre remontent en tête ; la saisie filtre code et nom. */
  const suggestions = useMemo(() => {
    const q = norm(query.trim());
    const isLocal = (n: HeatNetwork) => n.communes.some((c) => communes.includes(c));
    return networks
      .filter((n) => !selected.includes(n.id))
      .filter((n) => !q || norm(n.name).includes(q) || norm(n.code).includes(q))
      .sort((a, b) => Number(isLocal(b)) - Number(isLocal(a)))
      .slice(0, MAX_SUGGESTIONS)
      .map((n) => ({ network: n, local: isLocal(n) }));
  }, [networks, selected, communes, query]);

  const add = (id: string) => {
    onSelectedChange([...selected, id]);
    setQuery('');
    setOpen(false);
  };

  if (kind === 'new') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <UMSegmentedControl options={[KIND_LABEL.new, KIND_LABEL.extension]} value={KIND_LABEL.new} onChange={(v) => onKindChange(v === KIND_LABEL.extension ? 'extension' : 'new')} />
        <div style={{ ...card, background: UMR.grey100, display: 'flex', gap: 8, fontSize: 13, lineHeight: '18px' }}>
          <UMIcon name="add_circle" size={18} color={UMR.textSecondary} />
          <div>Aucun réseau existant : le projet part d’une page blanche.</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <UMSegmentedControl options={[KIND_LABEL.new, KIND_LABEL.extension]} value={KIND_LABEL.extension} onChange={(v) => onKindChange(v === KIND_LABEL.extension ? 'extension' : 'new')} />

      <div
        style={{ position: 'relative' }}
        onFocus={() => { clearTimeout(blurTimer.current); setOpen(true); }}
        onBlur={() => { blurTimer.current = setTimeout(() => setOpen(false), 120); }}
      >
        <UMInput
          value={query}
          onChange={(v) => { setQuery(v); setOpen(true); }}
          placeholder="Rechercher un code ou un nom de réseau…"
          ariaLabel="Rechercher un réseau de chaleur"
          icon="search"
        />
        {open && suggestions.length > 0 && (
          <ul
            role="listbox"
            aria-label="Réseaux proposés"
            style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30, margin: 0, padding: 0, listStyle: 'none',
              background: UMR.white, border: `1px solid ${UMR.borderInput}`, boxShadow: '0 4px 12px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto',
            }}
          >
            {suggestions.map(({ network: n, local }) => (
              <li key={n.id}>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => add(n.id)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', cursor: 'pointer',
                    border: 'none', borderBottom: `1px solid ${UMR.borderSubtle}`, background: 'transparent', textAlign: 'left', font: `400 13px/18px ${UMR.font}`,
                  }}
                >
                  <UMIcon name="hub" size={16} color={local ? PC.network : UMR.textSecondary} />
                  <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{n.code}</strong>
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.name}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {selected.length === 0 ? (
        <div style={{ ...caption, color: PC.dangerText }}>
          Au moins un réseau est nécessaire pour un projet de développement.
        </div>
      ) : (
        <ul style={chipListStyle}>
          {selected.map((id) => {
            const n = byId.get(id);
            return (
              <Chip
                key={id}
                label={n ? label(n) : id}
                removeLabel={`Retirer ${n?.name ?? id}`}
                onRemove={() => onSelectedChange(selected.filter((x) => x !== id))}
              />
            );
          })}
        </ul>
      )}
      <div style={caption}>Le tracé des réseaux sélectionnés s’affiche sur la carte.</div>
    </div>
  );
}
